/**
 * Which routes may open on an institute's own domain.
 *
 * One bundle serves every domain (see WHITE-LABEL-PLAN.md). On tr4u.in the
 * platform's marketing and demo pages must never appear, and a visitor who is
 * not signed in may only reach the public surface: the home page, sign-in, and
 * the token links an institute mails out (payments, invitations, onboarding).
 *
 * Plain functions with no React, so the rules are unit-tested on their own
 * (tenantRoutes.spec.ts) and TenantRouteGuard stays a thin adapter.
 *
 * WHY AN ALLOW-LIST FOR VISITORS
 * ------------------------------
 * A platform page added next year must not appear on a customer's domain by
 * default. For an anonymous visitor, anything not listed here goes to the
 * sign-in page. Signed-in members keep the whole app, minus PLATFORM_ONLY.
 */

/** Open to anyone on a tenant domain, signed in or not. */
export const TENANT_PUBLIC_ROUTES: readonly string[] = [
  '/',
  '/login',
  '/2fa',
  '/forgot-password',
  '/signup',
  '/signout',
  '/terms',
  '/privacy',
  // Links an institute sends to people who may not have an account yet.
  '/assessment/user/',
  '/assessments/user/',
  '/forms/user/',
  '/forms/users/',
  '/certificate',
  '/interactive/join',
  '/payment-request/',
  '/fee-payment-request/',
  '/batch-request/',
  '/qpaperOBE/invite/',
  '/employee-onboarding',
  '/employeeOnbard',
  // An asset's QR label (Assets & Finance): anyone who scans it may see the
  // tag/name and report a problem. Cost and holder are never shown here.
  '/a/',
  // An interactive course's free ~2-minute preview (the full course stays signed).
  '/scorm-demo/',
];

/**
 * The platform's own marketing and demo pages. Never on a tenant domain, for
 * anyone. (Sign-up is NOT here: it works on an institute's domain exactly as
 * on ours - the shared account, then "No access yet" until the institute
 * adds the person.)
 */
export const PLATFORM_ONLY_ROUTES: readonly string[] = [
  '/features',
  '/ourteam',
  // NOT /courses: on an institute's domain /courses is its website's Courses
  // list (the "More →" link), and App.tsx shows TenantWebsite there.
  '/testportal',
  '/test-portal',
  '/test-hub',
  '/test-demo',
  '/exam',
  '/flashcards-demo',
  '/text-to-speech',
  '/cli-auth',
  '/r/',
  '/affil/',
  '/ref/',
];

/**
 * Segment-aware match. An entry ending in "/" matches anything below it; any
 * other entry matches itself or itself followed by "/...". So "/exam" matches
 * "/exam" and "/exam/neet" but NOT "/examination", and "/" matches only "/".
 * An entry ending in "$" matches only itself ("/courses$": "/courses", never
 * "/courses/x").
 */
export const matchesRoute = (path: string, entry: string): boolean => {
  if (entry === '/') return path === '/';
  if (entry.endsWith('$')) return path === entry.slice(0, -1);
  if (entry.endsWith('/')) return path.startsWith(entry);
  return path === entry || path.startsWith(`${entry}/`);
};

const matchesAny = (path: string, entries: readonly string[]): boolean =>
  entries.some((entry) => matchesRoute(path, entry));

/**
 * null when `path` may render, otherwise where to send the browser instead.
 *
 *   platform host            always null; nothing changes there
 *   platform-only page       "/"       (their home, never our marketing)
 *   signed-in member         null      (the server enforces membership)
 *   public route             null
 *   anything else, visitor   "/login"
 */
export const tenantRedirectFor = (
  path: string,
  opts: { isPlatform: boolean; signedIn: boolean },
): string | null => {
  if (opts.isPlatform) return null;
  const clean = (path || '/').split('?')[0].split('#')[0] || '/';
  if (matchesAny(clean, PLATFORM_ONLY_ROUTES)) return '/';
  if (opts.signedIn) return null;
  if (matchesAny(clean, TENANT_PUBLIC_ROUTES)) return null;
  return '/login';
};

/** What master currently allows on this institute (branding endpoint `access`). */
export interface TenantAccess {
  state: string;
  publicSite: boolean;
  portal: boolean;
  message: string;
}

export const OPEN_ACCESS: TenantAccess = {
  state: 'active',
  publicSite: true,
  portal: true,
  message: '',
};

/** Pages that are the institute's PUBLIC site rather than its portal. */
export const TENANT_PUBLIC_SITE_ROUTES: readonly string[] = ['/', '/terms', '/privacy'];

/**
 * Whether master's switches hide this path, and how.
 *
 *   null       allowed
 *   'public'   the public site is off; the portal may still be open, so the
 *              unavailable page offers "Sign in"
 *   'all'      the portal is off for this path (or everything is), so the
 *              unavailable page offers nothing
 *
 * The server refuses the API calls regardless; this decides what is SHOWN.
 */
export const tenantAccessBlock = (
  path: string,
  access: TenantAccess,
): 'public' | 'all' | null => {
  const clean = (path || '/').split('?')[0].split('#')[0] || '/';
  const isPublicPage = matchesAny(clean, TENANT_PUBLIC_SITE_ROUTES);
  if (!access.portal && !access.publicSite) return 'all';
  if (isPublicPage) return access.publicSite ? null : 'public';
  return access.portal ? null : 'all';
};
